import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Collapse,
  SimpleGrid,
  VStack,
  HStack,
  useColorModeValue,
  Badge,
  Button,
  useToast,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { FiFileText, FiClock } from 'react-icons/fi';
import { formatServiceDateDisplay } from '../../../utils/dateOnly';
import { getData, postData } from '../../../utils/httpClient';
import api from '../../../services/api';

const getProductLabel = (used) =>
  used?.productName ||
  used?.product?.name ||
  used?.product?.code ||
  'Producto';

const isWarrantyService = (service) =>
  Number(service?.serviceTypeId) === 2 ||
  String(service?.serviceType?.name || service?.serviceName || '')
    .toLowerCase()
    .includes('garant');

const getAgendaStatus = (service) => {
  const agenda = Array.isArray(service?.agenda) ? service.agenda : [];
  const withStatus = agenda.find((a) => a?.status);
  return withStatus?.status || null;
};

const getServiceStatusLabel = (service) => {
  const fromRelation =
    service?.status?.description ||
    service?.status?.name ||
    (typeof service?.status === 'string' ? service.status : null);
  if (fromRelation) return String(fromRelation);
  const fromAgenda = getAgendaStatus(service);
  return fromAgenda ? String(fromAgenda) : '';
};

const getServiceStatusText = (service) => {
  const label = getServiceStatusLabel(service);
  const name = service?.status?.name || '';
  const description = service?.status?.description || '';
  const agenda = getAgendaStatus(service) || '';
  return `${label} ${name} ${description} ${agenda}`.toLowerCase();
};

const ServiceDetails = ({ services }) => {
  const [showServiceDetails, setShowServiceDetails] = useState(null);
  const [openingPdfId, setOpeningPdfId] = useState(null);
  const toast = useToast();

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const headerBg = useColorModeValue('gray.100', 'gray.700');

  const openPdfByDocumentId = async (documentId, label) => {
    if (!documentId) return;
    const response = await getData(`/document/${documentId}`, {
      skipLoader: true,
    });
    const document = response?.payload?.document;
    if (!document?.file) {
      throw new Error(`No se pudo abrir ${label}`);
    }
    const pdfData = new Uint8Array(document.file.data);
    const blob = new Blob([pdfData], { type: 'application/pdf' });
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const regenerateAndOpenPdf = async (serviceId, kind, label) => {
    if (!serviceId) return;
    const loadingKey = `${kind}-${serviceId}`;
    setOpeningPdfId(loadingKey);
    try {
      const endpoint =
        kind === 'reception'
          ? `/vehicleServices/${serviceId}/reception-pdf`
          : kind === 'warranty'
            ? `/vehicleServices/${serviceId}/warranty-pdf`
            : `/vehicleServices/${serviceId}/service-pdf`;
      const response = await postData(endpoint, {}, { skipLoader: true });
      const docId =
        response?.payload?.documentId ||
        response?.payload?.vehicleService?.documentId;
      if (!docId) {
        throw new Error('No se obtuvo el documento generado.');
      }
      await openPdfByDocumentId(docId, label);
    } catch (error) {
      toast({
        title: `No se pudo abrir ${label}`,
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Intentá de nuevo más tarde.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setOpeningPdfId(null);
    }
  };

  const [openingHistoryId, setOpeningHistoryId] = useState(null);

  const openVehicleHistoryPdf = async (vehicleId, plate) => {
    if (!vehicleId) return;
    setOpeningHistoryId(vehicleId);
    try {
      const response = await api.post(
        `/document/vehicle-history/${vehicleId}`,
        {},
        { responseType: 'arraybuffer' },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (blob.size === 0) {
        throw new Error('El PDF generado está vacío');
      }
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `historial_vehiculo_${plate || vehicleId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toast({
        title: 'No se pudo generar el historial',
        description:
          error?.response?.data?.message ||
          error?.message ||
          'Intentá de nuevo más tarde.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setOpeningHistoryId(null);
    }
  };

  const sortedServices = [...(Array.isArray(services) ? services : [])].sort(
    (a, b) => new Date(b.date || 0) - new Date(a.date || 0),
  );

  // Un botón por vehículo distinto (acá suele ser uno solo; en el historial
  // de un cliente puede haber varios vehículos mezclados).
  const vehiclesInList = Object.values(
    sortedServices.reduce((acc, s) => {
      if (s.vehicleId && !acc[s.vehicleId]) {
        acc[s.vehicleId] = { id: s.vehicleId, plate: s.vehicle?.plate };
      }
      return acc;
    }, {}),
  );

  const getStatusBadge = (service) => {
    const desc = getServiceStatusLabel(service);
    const lower = desc.toLowerCase();
    const colorScheme = lower.includes('complet') || lower.includes('finaliz')
      ? 'green'
      : lower.includes('cancel')
        ? 'red'
        : lower.includes('curso') || lower.includes('recepcion')
          ? 'blue'
          : 'gray';
    return (
      <Badge colorScheme={colorScheme} fontSize="xs" px={2} py={0.5}>
        {desc || 'Sin estado'}
      </Badge>
    );
  };

  const isCanceledService = (service) => {
    if (Number(service?.statusId) === 5) return true;
    return getServiceStatusText(service).includes('cancel');
  };

  const isPendingConfirmation = (service) => {
    if (Number(service?.statusId) === 1) return true;
    const text = getServiceStatusText(service);
    return (
      text.includes('pendiente de confirmación') ||
      text.includes('pendiente de confirmacion') ||
      (text.includes('pendiente') && !text.includes('curso'))
    );
  };

  const hideEventsAndProducts = (service) =>
    isCanceledService(service) || isPendingConfirmation(service);

  const canShowFinishedServicePdf = (service) => {
    if (isCanceledService(service)) return false;
    if (isPendingConfirmation(service)) return false;
    const statusId = Number(service?.statusId);
    if (statusId === 2 || statusId === 3) return false; // Confirmado / En curso
    const text = getServiceStatusText(service);
    if (
      text.includes('confirmado') ||
      text.includes('en curso') ||
      text.includes('recepcionado')
    ) {
      return false;
    }
    return true;
  };

  const groupProducts = (usedProducts = []) => {
    const map = {};
    usedProducts.forEach((item) => {
      const label = getProductLabel(item);
      const key = `${item.productId || label}`;
      if (!map[key]) {
        map[key] = {
          label,
          quantity: Number(item.quantity) || 0,
          price: item.price,
        };
      } else {
        map[key].quantity += Number(item.quantity) || 0;
      }
    });
    return Object.values(map);
  };

  return (
    <Box mt={2}>
      <Flex align="center" justify="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Text fontSize="md" fontWeight="semibold" color={valueColor}>
          Historial de Servicios ({sortedServices.length})
        </Text>
        <HStack spacing={2} flexWrap="wrap">
          {vehiclesInList.map((vehicle) => (
            <Button
              key={vehicle.id}
              size="xs"
              leftIcon={<FiClock />}
              colorScheme="purple"
              variant="outline"
              isLoading={openingHistoryId === vehicle.id}
              onClick={() => openVehicleHistoryPdf(vehicle.id, vehicle.plate)}
            >
              {vehiclesInList.length > 1
                ? `Ver historia en PDF · ${vehicle.plate || vehicle.id}`
                : 'Ver historia en PDF'}
            </Button>
          ))}
        </HStack>
      </Flex>

      {sortedServices.length > 0 ? (
        <VStack align="stretch" spacing={2}>
          {sortedServices.map((service) => {
            const isOpen = showServiceDetails === service.id;
            const events = service.vehicleServiceEventsHistories || [];
            const products = groupProducts(service.usedProducts || []);
            const kms =
              service.odometer != null && service.odometer !== ''
                ? `${service.odometer} km`
                : null;

            return (
              <Box
                key={service.id}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                overflow="hidden"
              >
                <Box
                  px={3}
                  py={2.5}
                  bg={isOpen ? headerBg : cardBg}
                  cursor="pointer"
                  onClick={() =>
                    setShowServiceDetails((prev) =>
                      prev === service.id ? null : service.id,
                    )
                  }
                  _hover={{ bg: headerBg }}
                >
                  <Flex align="center" justify="space-between" gap={3}>
                    <HStack spacing={3} flex="1" minW={0} flexWrap="wrap">
                      <Text fontWeight="semibold" fontSize="sm" whiteSpace="nowrap">
                        {formatServiceDateDisplay(service)}
                      </Text>
                      {service.time && (
                        <Text fontSize="xs" color={labelColor}>
                          {service.time}
                        </Text>
                      )}
                      <Text
                        fontSize="sm"
                        color={valueColor}
                        noOfLines={1}
                        flex="1"
                        minW="140px"
                      >
                        {service.description || 'Sin descripción'}
                      </Text>
                      {getStatusBadge(service)}
                    </HStack>
                    <IconButton
                      aria-label={isOpen ? 'Ocultar' : 'Mostrar'}
                      icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      variant="ghost"
                      size="xs"
                    />
                  </Flex>

                  {!isCanceledService(service) && (
                    <HStack
                      spacing={2}
                      mt={2}
                      justify="flex-start"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!isWarrantyService(service) && (
                        <Button
                          size="xs"
                          leftIcon={<FiFileText />}
                          colorScheme="blue"
                          variant="outline"
                          isLoading={openingPdfId === `reception-${service.id}`}
                          onClick={() =>
                            regenerateAndOpenPdf(
                              service.id,
                              'reception',
                              'Orden de reparación / recepción',
                            )
                          }
                        >
                          Orden de reparación
                        </Button>
                      )}
                      {canShowFinishedServicePdf(service) && (
                        <Button
                          size="xs"
                          leftIcon={<FiFileText />}
                          colorScheme="green"
                          variant="outline"
                          isLoading={openingPdfId === `service-${service.id}`}
                          onClick={() =>
                            regenerateAndOpenPdf(
                              service.id,
                              'service',
                              'Informe de servicio',
                            )
                          }
                        >
                          Informe de servicio
                        </Button>
                      )}
                    </HStack>
                  )}
                </Box>

                <Collapse in={isOpen}>
                  <Box px={3} py={3} bg="white">
                    {hideEventsAndProducts(service) ? (
                      <VStack align="stretch" spacing={3}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                          <Box>
                            <Text fontSize="xs" color={labelColor}>
                              Cliente
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {service.customer?.name || 'N/A'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={labelColor}>
                              Fecha
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {formatServiceDateDisplay(service)}
                              {service.time ? ` · ${service.time}` : ''}
                            </Text>
                          </Box>
                        </SimpleGrid>

                        {isCanceledService(service) && (
                          <Box
                            p={3}
                            bg="red.50"
                            borderRadius="md"
                            border="1px solid"
                            borderColor="red.200"
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color="red.600"
                              mb={1}
                              textTransform="uppercase"
                            >
                              Motivo de cancelación
                            </Text>
                            <Text fontSize="sm" color="red.700">
                              {service.canceledObservation ||
                                'Sin motivo registrado'}
                            </Text>
                          </Box>
                        )}

                        {(service.customerObservations ||
                          service.mechanicObservation ||
                          service.mechanicObservations ||
                          service.observations) && (
                          <Box>
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color={labelColor}
                              mb={2}
                              textTransform="uppercase"
                            >
                              Observaciones
                            </Text>
                            <VStack align="stretch" spacing={2}>
                              {service.customerObservations && (
                                <Box>
                                  <Text fontSize="xs" color={labelColor}>
                                    Cliente
                                  </Text>
                                  <Text fontSize="sm">
                                    {service.customerObservations}
                                  </Text>
                                </Box>
                              )}
                              {(service.mechanicObservation ||
                                service.mechanicObservations) && (
                                <Box>
                                  <Text fontSize="xs" color={labelColor}>
                                    {isWarrantyService(service)
                                      ? 'Observaciones finales'
                                      : 'Técnico'}
                                  </Text>
                                  <Text fontSize="sm">
                                    {service.mechanicObservation ||
                                      service.mechanicObservations}
                                  </Text>
                                </Box>
                              )}
                              {service.observations && (
                                <Box>
                                  <Text fontSize="xs" color={labelColor}>
                                    Generales
                                  </Text>
                                  <Text fontSize="sm">
                                    {service.observations}
                                  </Text>
                                </Box>
                              )}
                            </VStack>
                          </Box>
                        )}
                      </VStack>
                    ) : (
                      <>
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={3}>
                          <Box>
                            <Text fontSize="xs" color={labelColor}>
                              Cliente
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {service.customer?.name || 'N/A'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={labelColor}>
                              Técnico
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {service.mechanic?.name || 'N/A'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={labelColor}>
                              Km
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {kms || 'N/A'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color={labelColor}>
                              Costo VW
                            </Text>
                            <Text fontSize="sm" fontWeight="medium">
                              {service.volkswagenCost != null
                                ? `$${service.volkswagenCost}`
                                : 'N/A'}
                            </Text>
                          </Box>
                        </SimpleGrid>

                        {(service.customerObservations ||
                          service.mechanicObservation ||
                          service.mechanicObservations) && (
                          <VStack align="stretch" spacing={1} mb={3}>
                            {service.customerObservations && (
                              <Text fontSize="xs">
                                <Text as="span" fontWeight="semibold" color={labelColor}>
                                  Cliente:{' '}
                                </Text>
                                {service.customerObservations}
                              </Text>
                            )}
                            {(service.mechanicObservation ||
                              service.mechanicObservations) && (
                              <Text fontSize="xs">
                                <Text as="span" fontWeight="semibold" color={labelColor}>
                                  {isWarrantyService(service)
                                    ? 'Observaciones finales: '
                                    : 'Técnico: '}
                                </Text>
                                {service.mechanicObservation ||
                                  service.mechanicObservations}
                              </Text>
                            )}
                          </VStack>
                        )}

                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} alignItems="start">
                          {!isWarrantyService(service) && (
                          <Box>
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color={labelColor}
                              mb={2}
                              textTransform="uppercase"
                            >
                              Eventos ({events.length})
                            </Text>
                            {events.length > 0 ? (
                              <VStack align="stretch" spacing={1}>
                                {events.map((event) => (
                                  <Flex
                                    key={event.id || event.eventName}
                                    align="flex-start"
                                    justify="space-between"
                                    gap={2}
                                    py={1.5}
                                    px={2}
                                    borderBottom="1px solid"
                                    borderColor={borderColor}
                                  >
                                    <Box flex="1" minW={0}>
                                      <Text fontSize="xs" fontWeight="medium">
                                        {event.eventName ||
                                          event.event?.name ||
                                          'Evento'}
                                      </Text>
                                      {event.comment && (
                                        <Text fontSize="2xs" color={labelColor}>
                                          {event.comment}
                                        </Text>
                                      )}
                                    </Box>
                                    <HStack spacing={1} flexShrink={0}>
                                      <Badge
                                        fontSize="2xs"
                                        colorScheme={
                                          event.completed ? 'green' : 'orange'
                                        }
                                      >
                                        {event.completed ? 'OK' : 'NOK'}
                                      </Badge>
                                      <Badge
                                        fontSize="2xs"
                                        colorScheme={
                                          event.reparado ? 'green' : 'gray'
                                        }
                                      >
                                        {event.reparado ? 'SI' : 'NO'}
                                      </Badge>
                                    </HStack>
                                  </Flex>
                                ))}
                              </VStack>
                            ) : (
                              <Text fontSize="xs" color={labelColor}>
                                Sin eventos
                              </Text>
                            )}
                          </Box>
                          )}

                          <Box>
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color={labelColor}
                              mb={2}
                              textTransform="uppercase"
                            >
                              Productos ({products.length})
                            </Text>
                            {products.length > 0 ? (
                              <VStack align="stretch" spacing={1}>
                                {products.map((product) => (
                                  <Flex
                                    key={product.label}
                                    justify="space-between"
                                    align="center"
                                    gap={2}
                                    py={1.5}
                                    px={2}
                                    borderBottom="1px solid"
                                    borderColor={borderColor}
                                  >
                                    <Text fontSize="xs" fontWeight="medium">
                                      {product.label}
                                    </Text>
                                    <Badge colorScheme="purple" fontSize="2xs">
                                      x{product.quantity}
                                    </Badge>
                                  </Flex>
                                ))}
                              </VStack>
                            ) : (
                              <Text fontSize="xs" color={labelColor}>
                                Sin productos
                              </Text>
                            )}
                          </Box>
                        </SimpleGrid>
                      </>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </VStack>
      ) : (
        <Text color={labelColor} fontSize="sm">
          No hay servicios realizados para este vehículo.
        </Text>
      )}
    </Box>
  );
};

export default ServiceDetails;
